import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader2, Send, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { VoiceRecorder, getFileExtension } from '@/components/surveys/VoiceRecorder';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import type { SurveyTemplate, SurveyQuestion, SurveyRespondent } from '@/types/database';

interface SurveyResponseProps {}

export function SurveyResponse({}: SurveyResponseProps) {
  const { surveyId, respondentId } = useParams<{ surveyId: string; respondentId: string }>();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [respondent, setRespondent] = useState<SurveyRespondent | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [voiceResponses, setVoiceResponses] = useState<Record<string, Blob>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSurveyData();
  }, [surveyId, respondentId, user]);

  async function loadSurveyData() {
    if (!surveyId || !respondentId) {
      setLoadError('Invalid survey link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);

      // First check if user has organization access
      if (user) {
        const { data: orgAccess, error: orgError } = await supabase
          .from('survey_templates')
          .select(`
            *,
            questions:survey_questions(*),
            organization_id
          `)
          .eq('id', surveyId)
          .single();

        if (!orgError && orgAccess) {
          // Check if user has access to this organization
          const { data: hasAccess } = await supabase
            .from('organization_users')
            .select('organization_id')
            .eq('organization_id', orgAccess.organization_id)
            .eq('user_id', user.id)
            .maybeSingle();

          if (hasAccess) {
            setTemplate(orgAccess);
            setQuestions(orgAccess.questions || []);

            const initialAnswers: Record<string, string> = {};
            orgAccess.questions?.forEach((q: SurveyQuestion) => {
              initialAnswers[q.id] = '';
            });
            setAnswers(initialAnswers);

            const { data: respondentData, error: respondentError } = await supabase
              .from('survey_respondents')
              .select('*')
              .eq('id', respondentId)
              .single();

            if (respondentError) throw respondentError;
            setRespondent(respondentData);
            return;
          }
        }
      }

      // No organization access, check for valid invitation
      console.log('Checking for valid invitation...');
      const { data: invitation, error: invitationError } = await supabase
        .from('survey_invitations')
        .select('*')
        .eq('survey_id', surveyId)
        .eq('respondent_id', respondentId)
        .eq('status', 'sent')
        .maybeSingle();

      if (invitationError) {
        console.error('Error checking invitation:', invitationError);
        throw invitationError;
      }

      if (!invitation) {
        console.error('No valid invitation found');
        throw new Error('Invalid survey invitation');
      }

      if (invitation.expires_at && new Date(invitation.expires_at) < new Date()) {
        console.error('Invitation expired:', invitation.expires_at);
        throw new Error('This survey link has expired');
      }

      // Get survey template and questions
      const { data: templateData, error: templateError } = await supabase
        .from('survey_templates')
        .select(`
          *,
          questions:survey_questions(*)
        `)
        .eq('id', surveyId)
        .single();

      if (templateError) throw templateError;
      if (!templateData) throw new Error('Survey not found');

      // Get respondent info
      const { data: respondentData, error: respondentError } = await supabase
        .from('survey_respondents')
        .select('*')
        .eq('id', respondentId)
        .single();

      if (respondentError) throw respondentError;

      // Set states
      setTemplate(templateData);
      setQuestions(templateData.questions || []);
      setRespondent(respondentData);

      // Initialize empty answers
      const initialAnswers: Record<string, string> = {};
      templateData.questions?.forEach(q => {
        initialAnswers[q.id] = '';
      });
      setAnswers(initialAnswers);

    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  }

  const handleVoiceResponse = (questionId: string, audioBlob: Blob) => {
    setVoiceResponses(prev => ({
      ...prev,
      [questionId]: audioBlob
    }));
  };

  const validateAnswers = () => {
    const errors: Record<string, string> = {};
    questions.forEach(q => {
      const hasTextAnswer = answers[q.id]?.trim();
      const hasVoiceAnswer = !!voiceResponses[q.id];
      if (!hasTextAnswer && !hasVoiceAnswer) {
        errors[q.id] = 'This question requires an answer';
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setValidationAttempted(true);

    try {
      if (!validateAnswers()) {
        setSubmitting(false);
        const firstErrorId = questions.find(q => {
          const hasText = answers[q.id]?.trim();
          const hasVoice = !!voiceResponses[q.id];
          return !hasText && !hasVoice;
        })?.id;
        if (firstErrorId) {
          document.getElementById(`question-${firstErrorId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Submit responses
      const responses = await Promise.all(
        questions.map(async question => {
          const response = {
            template_id: surveyId,
            organization_id: respondent?.organization_id,
            respondent_id: respondentId,
            phone_number: respondent?.phone_number || '',
            question_id: question.id,
            answer: (answers[question.id] || '').trim(),
            voice_response_url: null as string | null
          };

          if (voiceResponses[question.id]) {
            const blob = voiceResponses[question.id];
            const ext = getFileExtension(blob.type);
            const filename = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
            const filePath = `${respondentId}/${filename}`;

            const { error: uploadError } = await supabase.storage
              .from('voice_responses')
              .upload(filePath, blob, { contentType: blob.type });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('voice_responses')
              .getPublicUrl(filePath);

            response.voice_response_url = publicUrl;
          }

          return response;
        })
      );

      const { error: responsesError } = await supabase
        .from('survey_responses')
        .insert(responses);

      if (responsesError) throw responsesError;

      // Update invitation status
      const { error: invitationError } = await supabase
        .from('survey_invitations')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('survey_id', surveyId)
        .eq('respondent_id', respondentId);

      if (invitationError) throw invitationError;

      setSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit survey');
      requestAnimationFrame(() => {
        document.getElementById('submit-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {loadError}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              If you believe this is an error, please contact your administrator.
            </p>
            <Button
              variant="outline"
              onClick={() => window.close()}
              className="w-full"
            >
              Close Window
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Thank you for completing the survey!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your responses have been recorded. You can now close this window.
            </p>
            <Button
              variant="outline"
              onClick={() => window.close()}
              className="w-full"
            >
              Close Window
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {template?.title}
            </h1>
            {template?.description && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {template.description}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {submitError && (
              <div id="submit-error" className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-start justify-between">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <p>{submitError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSubmitError(null)}
                  className="ml-4 flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {validationAttempted && Object.keys(validationErrors).length > 0 && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Please fix the following errors:</p>
                  <p className="mt-1">All questions must be answered before submitting the survey.</p>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {questions.map((question, index) => {
                const hasError = validationAttempted && validationErrors[question.id];
                return (
                  <div 
                    key={question.id}
                    id={`question-${question.id}`}
                    className={cn(
                      "space-y-4 rounded-lg p-4",
                      hasError && "bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {hasError && (
                        <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-1" />
                      )}
                      <p className={cn(
                        "text-lg font-medium",
                        hasError ? "text-red-700 dark:text-red-400" : "text-gray-900 dark:text-white"
                      )}>
                        {index + 1}. {question.text}
                      </p>
                    </div>

                    {question.type === 'multiple-choice' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => (
                          <label
                            key={optionIndex}
                            className={cn(
                              "flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer",
                              hasError 
                                ? "border-red-200 dark:border-red-800 hover:bg-red-50/50 dark:hover:bg-red-900/20"
                                : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            )}
                          >
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={option}
                              checked={answers[question.id] === option}
                              onChange={(e) => setAnswers({
                                ...answers,
                                [question.id]: e.target.value
                              })}
                              className={cn(
                                "h-4 w-4 border focus:ring-2",
                                hasError
                                  ? "text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 focus:ring-red-500"
                                  : "text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                              )}
                            />
                            <span className="text-gray-900 dark:text-white">
                              {option}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : question.type === 'multi-select' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, optionIndex) => {
                          const selected: string[] = (() => {
                            const val = answers[question.id];
                            if (!val) return [];
                            try { return JSON.parse(val); } catch { return []; }
                          })();
                          const isChecked = selected.includes(option);
                          return (
                            <label
                              key={optionIndex}
                              className={cn(
                                "flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer",
                                hasError
                                  ? "border-red-200 dark:border-red-800 hover:bg-red-50/50 dark:hover:bg-red-900/20"
                                  : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  const next = isChecked
                                    ? selected.filter(s => s !== option)
                                    : [...selected, option];
                                  setAnswers({
                                    ...answers,
                                    [question.id]: JSON.stringify(next)
                                  });
                                }}
                                className={cn(
                                  "h-4 w-4 rounded border focus:ring-2",
                                  hasError
                                    ? "text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 focus:ring-red-500"
                                    : "text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                                )}
                              />
                              <span className="text-gray-900 dark:text-white">
                                {option}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={(e) => setAnswers({
                            ...answers,
                            [question.id]: e.target.value
                          })}
                          rows={4}
                          className={cn(
                            "w-full rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2",
                            hasError
                              ? "border-red-200 dark:border-red-800 focus:ring-red-500"
                              : "border-gray-200 dark:border-gray-700 focus:ring-blue-500"
                          )}
                          placeholder="Enter your answer here..."
                        />
                        
                        <VoiceRecorder
                          onSave={async (audioBlob) => {
                            handleVoiceResponse(question.id, audioBlob);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.close()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Survey
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}